import { useContext, useEffect, useRef, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { authContext } from "../Context/Authentication/authContext";
import CityAttractions from "../CityAttractions/CityAttractions";
import PredictionsPopUp from "./PredictionsPopUp";
import "./Chart.scss";

am4core.addLicense("CH332271218");

am4core.useTheme(am4themes_animated);

const colorPalette = [
  "#f7b047",
  "#7dfc00",
  "#fc804e",
  "#fcff5d",
  "#04c7c7",
  "#f07cab",
  "#dadada",
  "#96341c",
  "#0099ba",
  "#da3800",
  "#44D492",
  "#ff5656",
  "#ad4cbe",
  "#8ec44f",
  "#ffcba5",
  "#3998f5",
  "#27aca0",
  "#F2A7C3",
  "#fbd60c",
  "#946aa2",
  "#FFA15C",
  "#0ec434",
  "#8ad8e8",
  "#d30b94",
  "#c56133",
  "#f47a22",
  "#277da7",
  "#b732cc",
  "#f22020",
  "#c3a5b4",
];

const usedColors = new Set<string>();

const formatDate = (
  date: any,
  addMonth: boolean,
  tab?: string,
  bulletIndex?: number,
  bulletLength?: number,
  yearlyDate?: any
) => {
  const year =
    tab == "Travel Time" ||
    tab == "Crime" ||
    tab == "Population" ||
    yearlyDate == "Yearly"
      ? date.getFullYear()
      : date.getFullYear();

  // Get month and pad with leading zero if needed
  let num = 1;
  if (addMonth) num = 2;
  let month = String(date.getMonth() + num).padStart(2, "0");
  if (tab) {
    month =
      tab == "Travel Time" ||
      tab == "Crime" ||
      tab == "Population" ||
      yearlyDate == "Yearly"
        ? bulletIndex == 0
          ? String(date.getMonth() + num).padStart(2, "0")
          : bulletLength && bulletIndex === bulletLength - 1
          ? "01"
          : "12"
        : String(date.getMonth() + num).padStart(2, "0");
  }

  // Get day and pad with leading zero if needed

  const day =
    tab == "Travel Time" ||
    tab == "Crime" ||
    tab == "Population" ||
    yearlyDate == "Yearly"
      ? bulletIndex == 0
        ? String(date.getDate()).padStart(2, "0")
        : bulletLength && bulletIndex === bulletLength - 1
        ? "01"
        : "31"
      : String(date.getDate()).padStart(2, "0");
  // Combine the parts to form the formatted date
  if (
    tab == "Travel Time" ||
    tab == "Crime" ||
    tab == "Population" ||
    yearlyDate == "Yearly"
  ) {
    return `${year}`;
  }
  return `${year}-${month}-${day}`;
};

const generateColor = () => {
  const getRandomValue = () =>
    Math.floor(Math.random() * 256)
      .toString(16)
      .padStart(2, "0");

  let red, green, blue;
  do {
    red = getRandomValue();
    green = getRandomValue();
    blue = getRandomValue();
  } while (
    parseInt(red, 16) + parseInt(green, 16) + parseInt(blue, 16) <
    128 * 3
  ); // Skip colors that are too dark

  return `#${red}${green}${blue}`;
};

const randColor = () => {
  let color;
  for (let i = 0; i < colorPalette.length; i++) {
    color = colorPalette[i];
    if (usedColors.has(color)) {
      continue;
    } else {
      usedColors.add(color);
      break;
    }
  }
  if (color === "#c3a5b4" && usedColors.size === colorPalette.length) {
    color = generateColor();
    usedColors.add(color);
  }
  return color;
};

const getText = (selectedOption: string, selectedTab: string) => {
  const removePredictionTextFor = [
    "Population",
    "Permits",
    "Rentals",
    "Travel Time",
    "Crime",
  ];
  const removeHelpfulTextFor = ["Crime"];

  let text = null;
  if (
    selectedOption === "predictedMetrics" ||
    selectedOption === "msaPredictedMetrics" || selectedOption === "countyPredictedMetrics" 
  ) {
    if (selectedTab === "Home sales") {
      text = (
        <div className="char-list-items">
          <ol>
            <li className="chart-list">
              <span className="heading">Absolute: </span>The plot shows total
              home sales for each month.
            </li>
            <li className="chart-list">
              <span className="heading">Growth (Month on Month): </span>The plot
              shows for month over month percentage change in total home sales.
            </li>
            <li className="chart-list">
              <span className="heading">Long Term Trend: </span>The plot shows
              the total sales for each month after adjusting for the volatility.
              (The total home sales are expected to vary closely to the shown in
              trending values)
            </li>
          </ol>
        </div>
      );
    } else if (selectedTab === "Days on market") {
      text = (
        <div className="char-list-items">
          <ol>
            <li className="chart-list">
              <span className="heading">Absolute: </span>The plot shows median
              days on market for a home sale for each month.
            </li>
            <li className="chart-list">
              <span className="heading">Growth (Month on Month): </span>The plot
              shows for month over month percentage change in median days on
              market for a home sale.
            </li>
            <li className="chart-list">
              <span className="heading">Long Term Trend: </span>The plot shows
              the median days on market for a home sale for each month after
              adjusting for the volatility. (The total home sales are expected
              to vary closely to the shown in trending values)
            </li>
          </ol>
        </div>
      );
    } else if (selectedTab === "Price") {
      text = (
        <div className="char-list-items">
          <ol>
            <li className="chart-list">
              <span className="heading">Absolute: </span>The plot shows median
              price per square foot for a home sale for each month.
            </li>
            <li className="chart-list">
              <span className="heading">Growth (Month on Month): </span>The plot
              shows for month over month percentage change in median price for
              square foot for a home sale.
            </li>
            <li className="chart-list">
              <span className="heading">Long Term Trend: </span>The plot shows
              the median price for square foot for a home sale for each month
              after adjusting for the volatility. (The total home sales are
              expected to vary closely to the shown in trending values)
            </li>
            <li className="chart-list">
              <span className="heading">Percentage: </span>The plot shows
              percentage change in price per square foot from the base date for
              each month.
            </li>
          </ol>
        </div>
      );
    } else if (selectedTab === "MSP") {
      text = (
        <div className="char-list-items">
          <ol>
            <li className="chart-list">
              <span className="heading">Absolute: </span>The plot shows median
              sale price for a home sale for each month.
            </li>
            <li className="chart-list">
              <span className="heading">Growth (Month on Month): </span> The
              plot shows for month over month percentage change in median sale
              price for a home sale.
            </li>
            <li className="chart-list">
              <span className="heading">Long Term Trend: </span>The plot shows
              the median sale price for a home sale for each month after
              adjusting for the volatility. (The total home sales are expected
              to vary closely to the shown in trending values)
            </li>
            <li className="chart-list">
              <span className="heading">Percentage: </span>The plot shows
              percentage change in median sale price from the base date for each
              month.
            </li>
          </ol>
        </div>
      );
    } else if (selectedTab === "Population") {
      text = (
        <div className="char-list-items">
          <ol>
            <li className="chart-list">
              The plot shows total population for each year.
            </li>
          </ol>
        </div>
      );
    } else if (selectedTab === "Permits") {
      text = (
        <div className="char-list-items">
          <ol>
            <li className="chart-list">
              <span className="heading">Total Buildings: </span>The plot shows
              permits issued for total buildings each month.
            </li>
            <li className="chart-list">
              <span className="heading">Total Units: </span>The plot shows
              permits issued for total units each month.
            </li>
            <li className="chart-list">
              <span className="heading">Total Valuation ($): </span>The plot
              shows permits issued for total valuation in dollar.
            </li>
            <li className="chart-list">
              <span className="heading">One Unit: </span>The plot shows permits
              issued for 1–units each month. (Each building accommodates a
              single family).
            </li>
            <li className="chart-list">
              <span className="heading">One Unit Valuation ($): </span>The plot
              shows one-unit permits issued for valuation in dollars.
            </li>
            <li className="chart-list">
              <span className="heading">Two Units: </span>The plot shows permits
              issued for 2 - units each month. (Each building accommodates two
              families).
            </li>
            <li className="chart-list">
              <span className="heading">Two Units Valuation ($): </span>The plot
              shows two-unit permits issued for valuation in dollars.
            </li>
            <li className="chart-list">
              <span className="heading">Three to Four Units: </span>The plot
              shows permits issued for 3 to 4 - units each month. (Each building
              accommodates three or four families).
            </li>
            <li className="chart-list">
              <span className="heading">
                Three to Four Units Valuation ($):{" "}
              </span>
              The plot shows three or four-unit permits issued for valuation in
              dollar.
            </li>
            <li className="chart-list">
              <span className="heading">More Than Five Unit: </span>The plot
              shows permits issued for more than 5 or more units each month.
              Each building accommodates five or more families).
            </li>
            <li className="chart-list">
              <span className="heading">
                More Than Five Unit Valuation ($):{" "}
              </span>
              The plot shows five or more-unit permits issued for valuation in
              dollars.
            </li>
            <li className="chart-list">
              <span className="heading">
                Total Units Vs Total Valuation ($):{" "}
              </span>
              The line plot shows permits issued for total units each month and
              bar plot show valuation of those units.
            </li>
            <li className="chart-list">
              <span className="heading">
                One Unit Vs One Unit Valuation ($):{" "}
              </span>
              The line plot shows permits issued for 1-units each month and bar
              plot show valuation of those units.
            </li>
            <li className="chart-list">
              <span className="heading">
                Two Units Vs Two Units Valuation ($):{" "}
              </span>
              The line plot shows permits issued for 2-units each month and bar
              plot show valuation of those units.
            </li>
            <li className="chart-list">
              <span className="heading">
                Three To Four Units Vs Three To Four Units Valuation ($):{" "}
              </span>
              The line plot shows permits issued for 3- or 4-units each month
              and bar plot show valuation of those units.
            </li>
            <li className="chart-list">
              <span className="heading">
                More Than Five Units Vs More Than Five Units Valuation ($):{" "}
              </span>
              The line plot shows permits issued for 5- or more units each month
              and bar plot show valuation of those units.
            </li>
          </ol>
        </div>
      );
    } else if (selectedTab === "Rentals") {
      text = (
        <div className="char-list-items">
          <ul>
            <li className="chart-list">
              The plot shows median rent for each month for all selected cities.
            </li>
          </ul>
        </div>
      );
    } else if (selectedTab === "Travel Time") {
      text = (
        <div className="char-list-items">
          <ul>
            <li className="chart-list">
              The plot shows median commute time to work for all selected
              cities.
            </li>
          </ul>
        </div>
      );
    } else if (selectedTab === "Months of Supply") {
      text = (
        <div className="char-list-items">
          <ol>
            <li className="chart-list">
              <span className="heading">
                Months of supply is a real estate metric that measures how long
                it would take to sell all the homes currently on the market.
                It's calculated by dividing the number of homes for sale by the
                number of homes sold in a given month.
              </span>
            </li>
          </ol>
        </div>
      );
    }
  } else if (selectedOption === "zipcodePredictedMetrics") {
    if (selectedTab === "Home sales") {
      text = (
        <div className="char-list-items">
          <ol>
            <li className="chart-list">
              <span className="heading">Absolute: </span>The plot shows total
              home sales for each quarter.
            </li>
            <li className="chart-list">
              <span className="heading">Growth (Quarter on Quarter): </span>The
              plot shows for quarter over quarter percentage change in total
              home sales.
            </li>
            <li className="chart-list">
              <span className="heading">Long Term Trend: </span>The plot shows
              the total sales for each month after adjusting for the volatility.
              (The total home sales are expected to vary closely to the shown in
              trending values)
            </li>
          </ol>
        </div>
      );
    } else if (selectedTab === "Days on market") {
      text = (
        <div className="char-list-items">
          <ol>
            <li className="chart-list">
              <span className="heading">Absolute: </span>The plot shows median
              days on market for a home sale for each quarter.
            </li>
            <li className="chart-list">
              <span className="heading">Growth (Quarter on Quarter): </span>The
              plot shows for month over quarter percentage change in median days
              on market for a home sale.
            </li>
            <li className="chart-list">
              <span className="heading">Long Term Trend: </span>The plot shows
              the median days on market for a home sale for each quarter after
              adjusting for the volatility. (The total home sales are expected
              to vary closely to the shown in trending values)
            </li>
          </ol>
        </div>
      );
    } else if (selectedTab === "Price") {
      text = (
        <div className="char-list-items">
          <ol>
            <li className="chart-list">
              <span className="heading">Absolute: </span>The plot shows median
              price per square foot for a home sale for each quarter.
            </li>
            <li className="chart-list">
              <span className="heading">Growth (Quarter on Quarter): </span>The
              plot shows for quarter over quarter percentage change in median
              price for square foot for a home sale.
            </li>
            <li className="chart-list">
              <span className="heading">Long Term Trend: </span>The plot how the
              median price for square foot for a home sale for each quarter
              after adjusting for the volatility. (The total home sales are
              expected to vary closely to the shown in trending values)
            </li>
            <li className="chart-list">
              <span className="heading">Percentage: </span>The plot shows
              percentage change in price per square foot from the base date for
              each month.
            </li>
          </ol>
        </div>
      );
    } else if (selectedTab === "MSP") {
      text = (
        <div className="char-list-items">
          <ol>
            <li className="chart-list">
              <span className="heading">Absolute: </span>The plot shows total
              median sale price for each quarter.
            </li>
            <li className="chart-list">
              <span className="heading">Growth (Quarter on Quarter): </span>The
              plot shows for quarter over quarter percentage change in median
              sale price for a home sale.
            </li>
            <li className="chart-list">
              <span className="heading">Long Term Trend: </span>The plot shows
              the median sale price for a home sale for each quarter after
              adjusting for the volatility. (The total home sales are expected
              to vary closely to the shown in trending values)
            </li>
            <li className="chart-list">
              <span className="heading">Percentage: </span>The plot shows
              percentage change in median sale price from the base date for each
              month.
            </li>
          </ol>
        </div>
      );
    }
  } else if (selectedOption === "cityClusters") {
    text = (
      <div className="char-list-items">
        <ol>
          <li className="chart-list">
            The clusters are based on similarity between cities of ppsf and
            population demographics.
          </li>
          <li className="chart-list">
            The plot shows median price for square foot for a home sale for each
            month for all the cities in cluster at a time.
          </li>
          <li className="chart-list">
            The map shows the locations of cities in the selected state with
            same color for all cities in same cluster.
          </li>
        </ol>
      </div>
    );
  }
  return (
    <div className="help-text">
      {!removePredictionTextFor.includes(selectedTab) && (
        <div>
          Predicted values represented by dashed line.
          <br />
          <br />
        </div>
      )}
      {!removeHelpfulTextFor.includes(selectedTab) && (
        <span style={{ textDecoration: "underline" }}>Helpful Hints:</span>
      )}
      {text}
    </div>
  );
};

const generateTable = (data: any) => {
  let tableHtml =
    '<table border="1"><tr><th>Feature</th><th>Importance</th></tr>';
  for (const item of data) {
    tableHtml += `<tr><td>${item.feature}</td><td>${item.importance}</td></tr>`;
  }
  tableHtml += "</table>";
  return tableHtml;
};

const getToolTipLabel = (tab: string, yearlyDate: string) => {
  return tab == "Travel Time" ||
    tab == "Crime" ||
    tab == "Population" ||
    yearlyDate == "Yearly"
    ? "Year"
    : "Date";
};

const generateToolTip = (
  value: any,
  xAisValue: any,
  item: any,
  percentageType: any,
  percentageTooltip: boolean,
  customTooltip: any,
  showInterval: any,
  zipcode: any,
  city: any,
  title: any,
  tab: string,
  bulletIndex: number,
  bulletLength: number,
  yearlyDate: string,
  perDiff: any,
  compare: boolean
) => {
  let tooltip = "<></>";

  if (percentageTooltip) {
    const currentSeriesAbs = `${value}_absolute`;
    const currentSeriesBaseDate = `${value}_base_date`;
    const currentSeriesBaseAbs = `${value}_base_value`;
    const currentSeriesLastYearDate = `${value}_last_year_date`;
    const currentSeriesLastYearAbs = `${value}_last_year_value`;
    const currentSeriesLastYearPer = `${value}_last_year_per`;
    const basePer = `${value}_base_per`;

    tooltip = `
      <div style='display: table; border-collapse: collapse; width: 100%; font-weight: bold;'>
        <div style='display: table-row;'>
          <div style='display: table-cell; padding: 5px; text-align: left;'>Name:</div>
          <div style='display: table-cell; padding: 5px; text-align: left;'>${item}</div>
        </div>
        <div style='display: table-row;'>
          <div style='display: table-cell; padding: 5px; text-align: left;'>Date:</div>
          <div style='display: table-cell; padding: 5px; text-align: left;'>{dateX.formatDate('MM-dd-yyyy')}</div>
        </div>
        <div style='display: table-row;'>
          <div style='display: table-cell; padding: 5px; text-align: left;'>${percentageType} Absolute Value($):</div>
          <div style='display: table-cell; padding: 5px; text-align: left;'>{valueY}</div>
        </div>
        <div style='display: table-row;'>
          <div style='display: table-cell; padding: 5px; text-align: left;'>Base Date:</div>
          <div style='display: table-cell; padding: 5px; text-align: left;'>{${currentSeriesBaseDate}.formatDate('MM-dd-yyyy')}</div>
        </div>
        <div style='display: table-row;'>
          <div style='display: table-cell; padding: 5px; text-align: left;'>Base ${percentageType} Absolute Value($):</div>
          <div style='display: table-cell; padding: 5px; text-align: left;'>{${currentSeriesBaseAbs}}</div>
        </div>
        <div style='display: table-row;'>
          <div style='display: table-cell; padding: 5px; text-align: left;'>% Growth From Base Date:</div>
          <div style='display: table-cell; padding: 5px; text-align: left;'>{${basePer}}</div>
        </div>

      </div>
    `;
  } else {
    if (customTooltip) {
      tooltip = `<div><div><span style='font-weight: bold'>${item}:</span> {valueY}</div><div><span style='font-weight: bold'>${getToolTipLabel(
        tab,
        yearlyDate
      )}:</span> ${formatDate(
        xAisValue,
        false,
        tab,
        bulletIndex,
        bulletLength,
        yearlyDate
      )}</div></div>`;
    } else {
      tooltip = `<div><div><span style='font-weight: bold'>${
        showInterval
          ? value === "value1"
            ? `${zipcode[0] ? zipcode[0] : city[0]} High`
            : value === "value2"
            ? `${zipcode[0] ? zipcode[0] : city[0]} Low`
            : value === "value3"
            ? `${zipcode[0] ? zipcode[0] : city[0]} Mean`
            : ""
          : title
      }:</span> {valueY}</div><div><span style='font-weight: bold'>${
        zipcode && zipcode[0] ? "Zipcode" : "City"
      }:</span> ${item}</div><div><span style='font-weight: bold'>${getToolTipLabel(
        tab,
        yearlyDate
      )}:</span> ${formatDate(
        xAisValue,
        false,
        tab,
        bulletIndex,
        bulletLength,
        yearlyDate
      )}</div>
      <div><div><span style='font-weight: bold'>${
        compare
          ? `% Diff with ${
              item.split(" ")[item.split(" ").length - 1] != "(Predicted)"
                ? "Predicted"
                : "Actual"
            } :`
          : ""
      }</span> ${
        compare
          ? item.split(" ")[item.split(" ").length - 1] === "(Predicted)"
            ? "{valueX}%"
            : "{valueZ}%"
          : ""
      } </div></div>`;
    }
  }

  return tooltip;
};

const Chart = ({
  chartData,
  city,
  title,
  zipcode,
  showInterval,
  indicators,
  sideText,
  zoomDate,
  sidePanelHeading,
  customTooltip,
  percentageTooltip,
  percentageType,
  yearlyDate,
  county,
}: any) => {
  
  usedColors.clear();
  const chartRef: any = useRef(null);
  const [cities, setCities]: any = useState([]);
  const { state, dispatch }: any = useContext(authContext);
  const predictionTill =
    state.filters.predictionTill[state.filters.state.toLowerCase()];
  const selectedOption = state.filters.selectedType;
  const selectedTab = state.selectedTab;
  const [selectedItem, setSelectedItem]: any = useState("");
  const [selectedDate, setSelectedDate]: any = useState("");
  const [open, setOpen]: any = useState(false);

  const closePopUp = () => {
    setOpen(false);
    setSelectedItem("");
    setSelectedDate("");
  };

  useEffect(() => {
    chartRef.current = am4core.create("chartDiv", am4charts.XYChart);

    chartRef.current.data = chartData;

    // Set input format for the dates
    chartRef.current.dateFormatter.inputDateFormat = "yyyy-MM-dd";

    chartRef.current.events.on("ready", function () {
      if (state.filters.chartStartDate && state.filters.chartEndDate) {
        dateAxis.zoomToDates(
          new Date(state.filters.chartStartDate),
          new Date(state.filters.chartEndDate)
        );
      }
    });

    // Create axes
    const dateAxis = chartRef.current.xAxes.push(new am4charts.DateAxis());
    dateAxis.renderer.grid.template.strokeWidth = 1;
    dateAxis.renderer.grid.template.strokeOpacity = 0.3;
    dateAxis.renderer.grid.template.stroke = "#fff";
    dateAxis.renderer.minGridDistance =
      state.selectedTab === "Population" ||
      state.selectedTab === "Travel Time" ||
      state.selectedTab === "Crime"
        ? 200
        : 100;
    dateAxis.title.text = "Date";
    dateAxis.title.fill = "#fff";
    dateAxis.title.fontSize = 13;
    dateAxis.renderer.labels.template.fontSize = 10;
    dateAxis.title.fontWeight = "bold";
    dateAxis.dateFormats.setKey("month", "MMM YYYY");
    dateAxis.renderer.labels.template.fill = "#fff";

    const dateAxisChanged = (ev: any) => {
      let start = ev.target.minZoomed;
      let end = ev.target.maxZoomed;
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          chartStartDate: start,
          chartEndDate: end,
        },
      });
    };
    dateAxis.events.on("startchanged", dateAxisChanged);
    dateAxis.events.on("endchanged", dateAxisChanged);

    const valueAxis = chartRef.current.yAxes.push(new am4charts.ValueAxis());
    valueAxis.renderer.grid.template.stroke = "#fff";
    valueAxis.renderer.grid.template.strokeWidth = 1;
    valueAxis.renderer.grid.template.strokeOpacity = 0.3;
    valueAxis.title.text = title;
    valueAxis.title.fill = "#fff";
    valueAxis.title.fontSize = 13;
    valueAxis.title.fontWeight = "bold";
    valueAxis.renderer.labels.template.fontSize = 10;
    valueAxis.renderer.labels.template.fill = "#fff";
    let cityData: any = [];

    const createSeries = (
      value: any,
      color: string,
      item: string,
      perDiff?: any,
      absDiff?: any
    ) => {
      if (title === "Scale") {
        indicators.forEach((val: any) => {
          if (val === item) cityData.push({ name: val, color: color });
        });
      } else if (showInterval) cityData.push({ name: item, color: color });
      else {
        (zipcode?.length ? zipcode : city).forEach((val: any) => {
          if (val === item) cityData.push({ name: val, color: color });
        });
      }
      setCities(cityData);
      const series = chartRef.current.series.push(new am4charts.LineSeries());
      series.dataFields.valueY = value;
      series.dataFields.dateX = "date";
      series.dataFields.valueX = perDiff;
      series.dataFields.valueZ = absDiff;
      series.strokeWidth = 2;
      if (state.filters.selectedType !== "citySpecificModels")
        series.dataItems.template.locations.dateX = 0;
      series.stroke = am4core.color(color);
      
      // Set up continuous tooltip
      series.tooltip.label.minWidth = 40;
      series.tooltip.label.minHeight = 40;
      series.tooltip.label.textAlign = "middle";
      series.tooltip.label.textValign = "middle";
      series.tooltip.label.backgroundColor = color;
      series.tooltip.label.color = "#fff";
      series.tooltip.label.fontSize = 10;
      series.sequencedInterpolation = true;
      series.hoverOnFocus = true;
      series.tooltip.getFillFromObject = false;
      series.tooltip.background.fill = am4core.color("#333");
      series.tooltip.getStrokeFromObject = true;
      series.tooltip.label.fill = am4core.color(color);
      
      // Set tooltip HTML based on different conditions
      if (percentageTooltip) {
        const currentSeriesAbs = `${value}_absolute`;
        const currentSeriesBaseDate = `${value}_base_date`;
        const currentSeriesBaseAbs = `${value}_base_value`;
        const basePer = `${value}_base_per`;

        series.tooltipHTML = `
          <div style='display: table; border-collapse: collapse; width: 100%; font-weight: bold;'>
            <div style='display: table-row;'>
              <div style='display: table-cell; padding: 5px; text-align: left;'>Name:</div>
              <div style='display: table-cell; padding: 5px; text-align: left;'>${item}</div>
            </div>
            <div style='display: table-row;'>
              <div style='display: table-cell; padding: 5px; text-align: left;'>Date:</div>
              <div style='display: table-cell; padding: 5px; text-align: left;'>{dateX.formatDate('MM-dd-yyyy')}</div>
            </div>
            <div style='display: table-row;'>
              <div style='display: table-cell; padding: 5px; text-align: left;'>${percentageType} Absolute Value($):</div>
              <div style='display: table-cell; padding: 5px; text-align: left;'>{valueY}</div>
            </div>
            <div style='display: table-row;'>
              <div style='display: table-cell; padding: 5px; text-align: left;'>Base Date:</div>
              <div style='display: table-cell; padding: 5px; text-align: left;'>{${currentSeriesBaseDate}.formatDate('MM-dd-yyyy')}</div>
            </div>
            <div style='display: table-row;'>
              <div style='display: table-cell; padding: 5px; text-align: left;'>Base ${percentageType} Absolute Value($):</div>
              <div style='display: table-cell; padding: 5px; text-align: left;'>{${currentSeriesBaseAbs}}</div>
            </div>
            <div style='display: table-row;'>
              <div style='display: table-cell; padding: 5px; text-align: left;'>% Growth From Base Date:</div>
              <div style='display: table-cell; padding: 5px; text-align: left;'>{${basePer}}</div>
            </div>
          </div>
        `;
      } else if (customTooltip) {
        series.tooltipHTML = `<div><div><span style='font-weight: bold'>${item}:</span> {valueY}</div><div><span style='font-weight: bold'>${getToolTipLabel(
          state.selectedTab,
          yearlyDate
        )}:</span> {dateX.formatDate('yyyy-MM-dd')}</div></div>`;
      } else {
        series.tooltipHTML = `<div><div><span style='font-weight: bold'>${
          showInterval
            ? value === "value1"
              ? `${zipcode[0] ? zipcode[0] : city[0]} High`
              : value === "value2"
              ? `${zipcode[0] ? zipcode[0] : city[0]} Low`
              : value === "value3"
              ? `${zipcode[0] ? zipcode[0] : city[0]} Mean`
              : ""
            : title
        }:</span> {valueY}</div><div><span style='font-weight: bold'>${
          zipcode && zipcode[0] ? "Zipcode" : "City"
        }:</span> ${item}</div><div><span style='font-weight: bold'>${getToolTipLabel(
          state.selectedTab,
          yearlyDate
        )}:</span> {dateX.formatDate('yyyy-MM-dd')}</div>
        ${state.filters.showComparison ? `<div><div><span style='font-weight: bold'>${
          `% Diff with ${
            item.split(" ")[item.split(" ").length - 1] != "(Predicted)"
              ? "Predicted"
              : "Actual"
          } :`
        }</span> ${
          item.split(" ")[item.split(" ").length - 1] === "(Predicted)"
            ? "{valueX}%"
            : "{valueZ}%"
        } </div></div>` : ""}`;
      }

      const bullet = series.bullets.push(new am4charts.CircleBullet());
      bullet.circle.strokeWidth = 1;
      bullet.circle.radius = 3;
      bullet.circle.fill = am4core.color(color);
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() - 2;
      let predictionDate = new Date(year, month, 1);
      if (title !== "Scale" && title !== "Permits") {
        let range = dateAxis.createSeriesRange(series);
        try {
          // @ts-ignore
          if (state.filters.selectedType === "predictedMetrics")
            predictionDate = predictionTill?.city || predictionDate;
          // @ts-ignore
          else predictionDate = predictionTill?.zip || predictionDate;
        } catch (e) {
          console.error(e);
        }

        range.date = predictionDate;
        range.endDate = new Date(year + 5, currentDate.getMonth(), 1);
        range.contents.strokeDasharray = "3, 3";
      }

      // Keep the bullet click event for popup functionality
      bullet.events.on("hit", async (event: any) => {
        const index = event.target.dataItem.index;
        const xAxisValue = event.target.dataItem.dateX;
        const xDate = new Date(xAxisValue);
        const pDate = new Date(predictionDate);
        
        if (xDate >= pDate) {
          setSelectedItem(item);
          setSelectedDate(formatDate(xDate, true));
          setOpen(true);
        }
      });

      const hs2 = series.segments.template.states.create("hover");
      hs2.properties.strokeWidth = 5;
      hs2.properties.fill = "#fff";
      series.segments.template.strokeWidth = 1;
      const legend = value.split("value");
      const lineNo = parseInt(legend[1]);
      if (legend.length > 0) {
        if (zipcode && zipcode.length) {
          if (showInterval) {
            if (lineNo === 1) series.name = `${zipcode[lineNo - 1]} High`;
            else if (lineNo === 2) series.name = `${zipcode[lineNo - 2]} Low`;
            else series.name = `${zipcode[lineNo - 3]} Mean`;
          } else if (
            state.filters.showComparison &&
            state?.filters?.selectedType != "msaPredictedMetrics"
          ) {
            if (lineNo === 1) series.name = `${zipcode} Actual`;
            else if (lineNo === 2) series.name = `${zipcode} Predict`;
          } else if (
            state.filters.showComparison &&
            state?.filters?.selectedType === "msaPredictedMetrics"
          ) {
            if (lineNo === 1) series.name = `${city} Actual`;
            else if (lineNo === 2) series.name = `${city} Predict`;
          } else series.name = zipcode[lineNo - 1];
        } else if (state.filters.showComparison) {
          if (lineNo === 1) series.name = `${city} Actual`;
          else if (lineNo === 2) series.name = `${city} Predict`;
        } else if (
          state?.filters?.selectedType === "msaPredictedMetrics" &&
          state.selectedTab === "Home sales"
        ) {
          series.name = value;
        } else {
          if (showInterval) {
            if (lineNo === 1) series.name = `${city[lineNo - 1]} High`;
            else if (lineNo === 2) series.name = `${city[lineNo - 2]} Low`;
            else series.name = `${city[lineNo - 3]} Mean`;
          } else {
            if (county) {
              series.name = county[lineNo - 1];
            } else {
              series.name = city[lineNo - 1];
            }
          }
        }
      } else {
        series.name = value;
      }

      return series;
    };
    
    const getCompareActualValue = () => {
      if (zipcode && zipcode.length) {
        return `${zipcode[0]} (Actual)`;
      } else {
        return `${city[0]} (Actual)`;
      }
    };
    const getComparePredictedValue = () => {
      if (zipcode && zipcode.length) {
        return `${zipcode[0]} (Predicted)`;
      } else {
        return `${city[0]} (Predicted)`;
      }
    };
    const categoryColors: any = {
      resale: "#4CAF50", // Green
      new_sale: "#2196F3", // Blue
      total_sales: "#FF9800", // Orange
    };

    // Helper function to format category names for display
    const formatCategoryName = (category: string) => {
      switch (category) {
        case "resale":
          return "Resale";
        case "new_sale":
          return "New Sales";
        case "total_sales":
          return "Total Sales";
        default:
          return category;
      }
    };
    let mainSeries: any = [];
    if (title === "Scale") {
      indicators?.forEach((item: any, i: number) => {
        // @ts-ignore
        const series = createSeries(`value${i + 1}`, randColor(), item);
        mainSeries.push(series);
        return series;
      });
    } else if (state.filters.showComparison) {
      const getCompareActualValue = () => {
        if (zipcode && zipcode.length) {
          return `${zipcode[0]} (Actual)`;
        } else {
          return `${city[0]} (Actual)`;
        }
      };

      const getComparePredictedValue = () => {
        if (zipcode && zipcode.length) {
          return `${zipcode[0]} (Predicted)`;
        } else {
          return `${city[0]} (Predicted)`;
        }
      };

      const items = [getCompareActualValue(), getComparePredictedValue()];
      items.forEach((item: any, i: number) => {
        const series = createSeries(
          `value${i + 1}`,
          randColor() as any,
          item,
          `value3`,
          `value4`
        );
        mainSeries.push(series);
      });
    } else if (
      state?.filters?.selectedType === "msaPredictedMetrics" &&
      state.selectedTab === "Home sales"
    ) {
      state.filters.homeSalesArray.forEach(
        (category: string, categoryIndex: any) => {
          state.filters.msa.forEach((msa: any, msaIndex: any) => {
            const fieldName = `${category}_${msa.name}`;
            const seriesName = `${msa.name} (${formatCategoryName(category)})`;
            const color = categoryColors[category] || randColor();

            // Create the series
            const series = createSeries(fieldName, color, seriesName);
            mainSeries.push(series);
          });
        }
      );
    } else if (!showInterval) {
      if (county) {
        county?.forEach((item: any, i: number) => {
          // @ts-ignore
          const series = createSeries(`value${i + 1}`, randColor(), item);
          mainSeries.push(series);
          return series;
        });
      } else {
        (zipcode?.length ? zipcode : city)?.forEach((item: any, i: number) => {
          // @ts-ignore
          const series = createSeries(`value${i + 1}`, randColor(), item);
          mainSeries.push(series);
          return series;
        });
      }
    } else if (showInterval) {
      [
        `${zipcode[0] ? zipcode[0] : city[0]} High`,
        `${zipcode[0] ? zipcode[0] : city[0]} Low`,
        `${zipcode[0] ? zipcode[0] : city[0]} Mean`,
      ]?.forEach((item: any, i: number) => {
        // @ts-ignore
        const series = createSeries(`value${i + 1}`, randColor(), item);
        mainSeries.push(series);
        return series;
      });
    }

    if (!chartRef.current.scrollbarX) {
      var scrollbarX = new am4charts.XYChartScrollbar();
      scrollbarX.background.fill = am4core.color("#fff");
      scrollbarX.background.fillOpacity = 0.2;
      scrollbarX.startGrip.icon.stroke = am4core.color("#fff");
      scrollbarX.endGrip.icon.stroke = am4core.color("#fff");

      // Add the first few series to the scrollbar (to avoid too many)
      const seriesCount = Math.min(mainSeries.length, 3);
      for (let i = 0; i < seriesCount; i++) {
        scrollbarX.series.push(mainSeries[i]);
      }

      chartRef.current.scrollbarX = scrollbarX;
      chartRef.current.scrollbarX.parent = chartRef.current.bottomAxesContainer;
      chartRef.current.scrollbarX.startGrip.background.states.getKey(
        "hover"
      ).properties.fill = am4core.color("#fff");
      chartRef.current.scrollbarX.endGrip.background.states.getKey(
        "hover"
      ).properties.fill = am4core.color("#fff");
      chartRef.current.scrollbarX.startGrip.background.fill =
        am4core.color("#F7B047");
      chartRef.current.scrollbarX.endGrip.background.fill =
        am4core.color("#F7B047");
      chartRef.current.scrollbarX.thumb.background.fill =
        am4core.color("#171d25");

      let scrollSeries =
        chartRef.current.scrollbarX.scrollbarChart.series.getIndex(0);
      if (scrollSeries) {
        scrollSeries.stroke = "#000";
      }

      chartRef.current.scrollbarX.background.fill = am4core.color("#fff");
      chartRef.current.scrollbarX.background.fillOpacity = 0.2;
      chartRef.current.scrollbarX.unselectedOverlay.fill =
        am4core.color("#fff");
      chartRef.current.scrollbarX.unselectedOverlay.fillOpacity = 0.5;
    }

    // Add cursor for continuous tooltip functionality
    chartRef.current.cursor = new am4charts.XYCursor();
    chartRef.current.cursor.xAxis = dateAxis;

    // Add legend
    chartRef.current.legend = new am4charts.Legend();
    chartRef.current.legend.itemContainers.template.events.on(
      "over",
      (event: any) => {
        const segments = event.target.dataItem.dataContext.segments;
        segments.each((segment: any) => {
          segment.isHover = true;
        });
      }
    );

    chartRef.current.legend.itemContainers.template.events.on(
      "out",
      (event: any) => {
        const segments = event.target.dataItem.dataContext.segments;
        segments.each((segment: any) => {
          segment.isHover = false;
        });
      }
    );

    return () => {
      chartRef.current && chartRef.current?.dispose();
    };
  }, [chartData]);

  return (
    <>
      <div className="chart-main">
        <div
          className="chart-main__multilineAreaChart"
          id={"chartDiv"}
          ref={chartRef}
        ></div>
      </div>
      {title !== "Scale" && (
        <>
          {selectedOption === "predictedMetrics" && <CityAttractions />}
          <div className="chart-main__predictedValueText">
            {getText(selectedOption, selectedTab)}
          </div>
        </>
      )}
      <PredictionsPopUp
        item={selectedItem}
        date={selectedDate}
        open={open}
        closePopUp={closePopUp}
        showInterval={showInterval}
      />
    </>
  );
};

export default Chart;